import { RefObject, useEffect, useState } from "react";
import logo from "assets/mysher_logo.svg";
import { waitForImages } from "lib/preloadAssets";

const MIN_VISIBLE_MS = 900;
const FADE_MS = 500;

interface SplashScreenProps {
  contentRef: RefObject<HTMLDivElement | null>;
  onReady: () => void;
}

const SplashScreen = ({ contentRef, onReady }: SplashScreenProps) => {
  const [progress, setProgress] = useState(0);
  const [fadingOut, setFadingOut] = useState(false);

  useEffect(() => {
    const start = performance.now();
    const root = contentRef.current;
    const total = root ? root.querySelectorAll("img").length : 0;
    let loaded = 0;

    const bump = () => {
      loaded += 1;
      if (total > 0) setProgress(Math.min(100, Math.round((loaded / total) * 100)));
    };

    const fontsReady =
      typeof document !== "undefined" && "fonts" in document
        ? document.fonts.ready
        : Promise.resolve();

    Promise.all([waitForImages(root, bump), fontsReady]).then(() => {
      const elapsed = performance.now() - start;
      const remaining = Math.max(0, MIN_VISIBLE_MS - elapsed);
      window.setTimeout(() => {
        setProgress(100);
        setFadingOut(true);
        window.setTimeout(onReady, FADE_MS);
      }, remaining);
    });
  }, [contentRef, onReady]);

  return (
    <div
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-brand-dark transition-opacity duration-500 ${
        fadingOut ? "opacity-0" : "opacity-100"
      }`}
    >
      <img
        src={logo}
        alt="Mysher Aviary"
        className="h-24 w-24 animate-spin drop-shadow-[0_0_30px_rgba(245,166,35,0.35)]"
        style={{ animationDuration: "1.6s" }}
      />
      <div className="flex flex-col items-center gap-2">
        <div className="h-1 w-40 overflow-hidden rounded-full bg-white/10">
          <div
            className="h-full rounded-full bg-brand-amber transition-[width] duration-200"
            style={{ width: `${progress}%` }}
          />
        </div>
        <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/50">
          Loading Mysher Aviary…
        </p>
      </div>
    </div>
  );
};

export default SplashScreen;
